import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { BehaviorSubject, lastValueFrom, map, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { BackendApiService } from 'src/app/core/services/backend-api';
import { updatePeName, updateRoName } from 'src/app/core/store/rfqSummary.action';
import { DropDownOptions, StoreDropdowns } from 'src/app/shared/models/app-models';
import { PurchaseExecutiveListResponse, PurchaseExecutiveUser } from 'src/app/shared/models/database-models/PurchaseExecutivesReport.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SourcingOutcomeInformation, RFQSummaryRole } from 'src/app/shared/models/app-models/SourcingOutcome.model';
import { incrementLoading, decrementLoading } from 'src/app/core/store/loading.action';
import { LoadingState } from 'src/app/core/store/loading.reducer';

@Component({
  selector: 'app-reassign-dialog',
  templateUrl: './reassign-dialog.component.html',
  styleUrls: ['./reassign-dialog.component.scss']
})
export class ReassignDialogComponent {
  isReassignCompLoading: boolean = false;
  destroy$: Subject<boolean> = new Subject<boolean>();
  userDropdown: DropDownOptions[] = [];
  private fetchedPurchaseExecutivesList = new BehaviorSubject<
    PurchaseExecutiveUser[] | null>(null);
  public purchaseExecutivesList$ =
    this.fetchedPurchaseExecutivesList.asObservable();
  filtersForm = new FormGroup({
    purchaseExecutives: new FormControl<string[]>([], { nonNullable: true }),
  });

  PEDropDown: DropDownOptions[] = [];
  RODropDown: DropDownOptions[] = [];
  selectedPE: DropDownOptions | null = null;
  selectedRO: DropDownOptions | null = null;
  rfqSummary$: Observable<SourcingOutcomeInformation>;
  rfqSummaryDetails: SourcingOutcomeInformation = {} as SourcingOutcomeInformation;
  private loadingSubscription: Subscription = new Subscription();

  userRoleOnRfqSummary!: string;
  RFQSummaryRole = RFQSummaryRole;
  loadingMessage: string = '';

  constructor(@Inject(MAT_DIALOG_DATA) public data: any, private BackendApi: BackendApiService, private dialogRef: MatDialogRef<ReassignDialogComponent>, private store: Store<{
    dp: StoreDropdowns,
    rfqSummary: SourcingOutcomeInformation,
    loading: LoadingState
  }>, private _snackBar: MatSnackBar) {
    this.loadingSubscription = this.store.select('loading').subscribe((loadingState) => {
      this.isReassignCompLoading = loadingState.loadingStates['reassignrfqSummary'] > 0;
    });
    this.store
      .select('dp')
      .pipe(takeUntil(this.destroy$))
      .subscribe((dp: StoreDropdowns) => {
        console.log('Store dp:', dp);
        this.RODropDown = dp.UserList; // Assign RO options from store
        this.handledropdownOptions();
      });

    this.purchaseExecutivesList$
      .pipe(takeUntil(this.destroy$))
      .subscribe((peList) => {
        if (peList) {
          this.PEDropDown = peList.map((pe) => ({
            id: pe.Username?.toLowerCase(),
            value: pe.DisplayName,
            groupBy: 'Select All',
          }));
          this.filtersForm.get('purchaseExecutives')!.setValue(this.PEDropDown.map(item => item.id));
          this.handledropdownOptions();
        }
      });

    this.userRoleOnRfqSummary = this.data.userRoleOnRfqSummary;
    this.rfqSummary$ = this.store.select('rfqSummary');
    this.rfqSummary$
      .pipe(takeUntil(this.destroy$))
      .subscribe((summaryData: any) => {
        this.rfqSummaryDetails = summaryData as SourcingOutcomeInformation;
      });
  }

  async ngOnInit(): Promise<void> {
    await this.fetchPurchaseExecutivesList();
  }

  handledropdownOptions() {
    const isROOrBoth = this.userRoleOnRfqSummary === RFQSummaryRole.RO || this.userRoleOnRfqSummary === RFQSummaryRole.Both_PE_RO;
    const isPEOrBoth = this.userRoleOnRfqSummary === RFQSummaryRole.Assigned_PE || this.userRoleOnRfqSummary === RFQSummaryRole.Assigned_PE;
    const isOtherPE = this.userRoleOnRfqSummary === RFQSummaryRole.Other_PE;

    if (isROOrBoth || isPEOrBoth) {
      this.RODropDown = this.RODropDown.map((option: DropDownOptions) => ({
        ...option,
        disabled: option.id === this.rfqSummaryDetails.ROName
      }));
    }

    if (isPEOrBoth || isOtherPE) {
      this.PEDropDown = this.PEDropDown.map((option: DropDownOptions) => ({
        ...option,
        disabled: option.id === this.rfqSummaryDetails.AssignedPE
      }));
    }

    this.selectedPE = this.PEDropDown.find(option => option.id === this.rfqSummaryDetails.AssignedPE) ?? null;
    this.selectedRO = this.RODropDown.find(option => option.id === this.rfqSummaryDetails.ROName) ?? null;
  }

  openSnackBar(notifMsg: string, type: string) {
    this._snackBar.open(notifMsg, undefined, {
      verticalPosition: "top",
      panelClass: type == 'success' ? 'snackbar-popup' : 'app-notification-error',
      duration: 3000
    });
  }

  onPESelected(selectedPE: any) {
    this.selectedPE = selectedPE;
  }

  onROSelected(selectedRO: any) {
    this.selectedRO = selectedRO;
  }

  async onReassign() {
    if (this.selectedRO && this.selectedRO.id !== this.rfqSummaryDetails.ROName) {
      const payload = {
        "RFQSummaryNumber": this.data.RFQSummaryNumber,
        "ROName": this.selectedRO.id
      };
      try {
        this.loadingMessage = 'Reassigning InProgress...';
        this.store.dispatch(incrementLoading({ context: 'reassignrfqSummary' }));
        await lastValueFrom(
          this.BackendApi.reassignRO(payload).pipe(
            takeUntil(this.destroy$),
            map((response) => {
              if (response.status === 200) {
                this.openSnackBar(`Reassigned RO successfully to ${this.selectedRO?.value}`, 'success');
                this.store.dispatch(updateRoName({ ROName: this.selectedRO?.id || '' }));
              }
            })
          )
        );
      } catch (err) {
        this.openSnackBar('RO Reassign Failed', 'error');
      } finally {
        this.store.dispatch(decrementLoading({ context: 'reassignrfqSummary' }));
      }
    }
    if (this.selectedPE && this.selectedPE.id !== this.rfqSummaryDetails.AssignedPE) {
      const payload = {
        "RFQSummaryNumber": this.data.RFQSummaryNumber,
        "AssignedPE": this.selectedPE.id,
        "MPDocumentNumber": this.data.MPDocumentNumber
      };
      try {
        this.loadingMessage = 'Reassigning InProgress...';
        this.store.dispatch(incrementLoading({ context: 'reassignrfqSummary' }));
        await lastValueFrom(
          this.BackendApi.reassignPE(payload).pipe(
            takeUntil(this.destroy$),
            map((response) => {
              if (response.status === 200) {
                this.openSnackBar(`Reassigned PE successfully to ${this.selectedPE?.value}`, 'success');
                this.store.dispatch(updatePeName({ AssignedPE: this.selectedPE?.id || '' }));
              }
            })
          )
        );
      } catch (err) {
        this.openSnackBar('PE Reassign Failed', 'error');
      }
      finally {
        this.store.dispatch(decrementLoading({ context: 'reassignrfqSummary' }));
      }
    }
    this.dialogRef.close(true);
  }

  //Additional functions to retrieve list of PEs
  async fetchPurchaseExecutivesList() {
    this.store.dispatch(incrementLoading({ context: 'reassignrfqSummary' }));
    try {
      const response: PurchaseExecutiveListResponse = await lastValueFrom(
        this.BackendApi.getPurchaseExecutives()
      );

      if (!response || !Array.isArray(response)) {
        throw new Error('Empty response or not array of PEs');
      }

      this.fetchedPurchaseExecutivesList.next(response);
    } catch (err: any) {
      console.log(
        'ReportsPage fetchPurchaseExecutivesList failed due to error',
        err
      );
    } finally {
      this.store.dispatch(decrementLoading({ context: 'reassignrfqSummary' }));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
    this.loadingSubscription.unsubscribe();
  }

}
